import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  Chip,
  TablePagination,
  Menu,
  MenuItem,
  ListItemIcon,
  Checkbox,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Backdrop,
} from '@mui/material';
import moment from 'moment-timezone';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  deleteItem,
  createItem,
  updateItem,
  createItemModifierGroup,
} from '../../../redux/actions/posMenu';
import {
  getTranslationValue,
  SOLD_OUT_STATUS,
  getSoldOutDate,
} from '../../../constants/posMenu';
import { isPosProviderConnected } from '../../../utils/checkAccessDenied';
import { filteringSoldOut } from '../../../utils/soldOut';
import { getItemCategoryId } from '../../../utils/posMenuCategories';
import usePosMenuMap from '../hooks/usePosMenuMap';
import MenuLoadError from '../../../components/PosMenu/MenuLoadError';

const PosMenuItems = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { menuMap, internalMenuId } = useSelector((state) => state.posMenu);
  const {
    items: providerItems,
    loading: providersLoading,
    providerId,
  } = useSelector((state) => state.providers);
  const currentBusiness = useSelector(
    (state) => state.business?.currentBusiness
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [duplicatingItemId, setDuplicatingItemId] = useState(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateSourceItem, setDuplicateSourceItem] = useState(null);
  const [duplicateName, setDuplicateName] = useState('');
  const [duplicateTitle, setDuplicateTitle] = useState('');
  const [availabilityMenuAnchor, setAvailabilityMenuAnchor] = useState(null);
  const [availabilityMenuItem, setAvailabilityMenuItem] = useState(null);
  const [updatingAvailabilityItemId, setUpdatingAvailabilityItemId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkActionType, setBulkActionType] = useState(null);
  const [bulkPrice, setBulkPrice] = useState('');
  const [bulkAvailability, setBulkAvailability] = useState('');
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  // Get business timezone
  const businessTimezone = currentBusiness?.timezone?.timeZoneId;

  // Get business ID from localStorage
  const businessId = JSON.parse(localStorage.getItem('businessInfo'))?.id;

  // Check if providers have been loaded
  const providersLoaded = providerId !== null || providerItems?.length > 0;

  // Check if POS is connected
  const isPosConnected = isPosProviderConnected(providerItems);

  const { loadStatus: menuLoadStatus, retry: retryMenuLoad } =
    usePosMenuMap(businessId);

  // Convert items object to array
  const allItems = menuMap?.items ? Object.values(menuMap.items) : [];

  // Filter items based on search term (name or internal name/title)
  const filteredItems = allItems.filter((item) => {
    const name = getTranslationValue(item.name).toLowerCase();
    const title = getTranslationValue(item.title).toLowerCase();
    const query = searchTerm.toLowerCase();
    return name.includes(query) || title.includes(query);
  });

  // Sort items by name
  const sortedItems = [...filteredItems].sort((a, b) => {
    const nameA = getTranslationValue(a.name).toLowerCase();
    const nameB = getTranslationValue(b.name).toLowerCase();
    return nameA.localeCompare(nameB);
  });

  // Paginate
  const paginatedItems = sortedItems.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Deleting the only row on the last page would leave an empty page
  const lastPage = Math.max(0, Math.ceil(sortedItems.length / rowsPerPage) - 1);
  useEffect(() => {
    if (page > lastPage) setPage(lastPage);
  }, [page, lastPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditItem = (itemId) => {
    history.push(`/pos-menu/items/${itemId}`);
  };

  const handleAddItem = () => {
    history.push('/pos-menu/items/new');
  };

  const handleOpenDeleteDialog = (item) => {
    setDeletingItem(item);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeletingItem(null);
  };

  const handleDeleteItem = async () => {
    if (!deletingItem) return;

    setIsDeleting(true);
    try {
      const deletedId = deletingItem._id;
      await dispatch(deleteItem(deletedId));
      setSelectedIds((prev) => {
        if (!prev.has(deletedId)) return prev;
        const next = new Set(prev);
        next.delete(deletedId);
        return next;
      });
      toast.success('Item deleted successfully');
      handleCloseDeleteDialog();
    } catch (error) {
      toast.error(error.message || 'An error occurred');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenDuplicateDialog = (item) => {
    if (!internalMenuId) {
      toast.error('Menu information is not loaded. Please refresh the page.');
      return;
    }
    const originalName = getTranslationValue(item.name);
    const originalTitle = getTranslationValue(item.title);
    setDuplicateSourceItem(item);
    setDuplicateName(originalName);
    setDuplicateTitle(originalTitle || '');
    setDuplicateDialogOpen(true);
  };

  const handleCloseDuplicateDialog = () => {
    setDuplicateDialogOpen(false);
    setDuplicateSourceItem(null);
    setDuplicateName('');
    setDuplicateTitle('');
  };

  const handleConfirmDuplicate = async () => {
    if (!duplicateSourceItem || !internalMenuId) return;
    if (!duplicateName.trim()) {
      toast.error('Name is required');
      return;
    }

    const item = duplicateSourceItem;
    setDuplicatingItemId(item._id);
    handleCloseDuplicateDialog();

    try {
      // Build name object with user-provided name
      const nameObj = { en: duplicateName.trim() };

      // Build image object properly - handle various possible structures
      let imageObj = { url: '', fileId: '' };
      if (item.image) {
        imageObj = {
          url: item.image.url || '',
          fileId: item.image.fileId || item.image.key || '',
        };
      }

      // Helper to ensure translation objects have proper structure
      const ensureTranslationObj = (obj) => {
        if (!obj || typeof obj !== 'object' || !('en' in obj)) {
          return { en: '' };
        }
        return obj;
      };

      // Build title object if provided
      const titleObj = duplicateTitle.trim() ? { en: duplicateTitle.trim() } : { en: '' };

      // Menu-map items don't carry their category; it's on the category's itemIds
      const categoryId = getCategoryId(item);

      const duplicateData = {
        name: nameObj,
        title: titleObj,
        description: ensureTranslationObj(item.description),
        price: item.price || 0,
        tax: item.tax ?? null,
        sort: 0,
        tags: item.tags || [],
        isOnePrice: item.isOnePrice || false,
        image: imageObj,
        soldOut: { status: 'available', soldOutDate: null },
        onItsOwn: item.onItsOwn || false,
        isSizeOption: item.isSizeOption || false,
        kitchenInstruction: ensureTranslationObj(item.kitchenInstruction),
        recipe: ensureTranslationObj(item.recipe),
        minAllowedQuantity: item.minAllowedQuantity ?? null,
        maxAllowedQuantity: item.maxAllowedQuantity ?? null,
        minTotalNumberOfModifierGroups: item.minTotalNumberOfModifierGroups ?? null,
        maxTotalNumberOfModifierGroups: item.maxTotalNumberOfModifierGroups ?? null,
        minTotalNumberOfModifierItems: item.minTotalNumberOfModifierItems ?? null,
        maxTotalNumberOfModifierItems: item.maxTotalNumberOfModifierItems ?? null,
        enforceQtyStrict: item.enforceQtyStrict ?? false,
        carbs: item.carbs ?? 0,
        proteins: item.proteins ?? 0,
        calories: item.calories ?? 0,
        bulkInfo: item.bulkInfo,
        isPopular: item.isPopular || false,
        isPermanentlyPopular: item.isPermanentlyPopular || false,
        popularStartDate: item.popularStartDate ?? null,
        popularEndDate: item.popularEndDate ?? null,
        priceType: item.priceType ?? null,
        hideNameOnOrder: item.hideNameOnOrder ?? false,
        showNestedLayout: item.showNestedLayout ?? null,
        inOrderViewLayout: item.inOrderViewLayout ?? null,
        availableInKiosk: item.availableInKiosk ?? false,
        scheduleIds: item.scheduleIds || [],
        printerTags: item.printerTags || [],
        menu: internalMenuId,
        businessUuid: currentBusiness?.uuid,
        ...(categoryId && { category: categoryId }),
      };

      const createdItem = await dispatch(createItem(duplicateData));

      // Copy modifier groups from the original item
      const originalModifierGroups = item.modifierGroupIds || [];
      let failedModifierGroups = 0;
      for (let i = 0; i < originalModifierGroups.length; i++) {
        try {
          await dispatch(
            createItemModifierGroup({
              itemId: createdItem._id,
              modifierGroupId: originalModifierGroups[i],
              menuId: internalMenuId,
              sort: i,
            })
          );
        } catch (mgError) {
          // Continue with other modifier groups even if one fails
          failedModifierGroups += 1;
        }
      }

      if (failedModifierGroups > 0) {
        toast.warning(
          `Item "${nameObj.en}" created, but ${failedModifierGroups} of ${originalModifierGroups.length} modifier group(s) couldn't be copied`
        );
      } else {
        const mgMessage =
          originalModifierGroups.length > 0
            ? ` with ${originalModifierGroups.length} modifier group(s)`
            : '';
        toast.success(`Item "${nameObj.en}" created successfully${mgMessage}`);
      }
    } catch (error) {
      console.error('Failed to duplicate item:', error);
      toast.error(error.message || 'Failed to duplicate item');
    } finally {
      setDuplicatingItemId(null);
    }
  };

  const getCategoryId = (item) => getItemCategoryId(item._id, menuMap);

  const getCategoryName = (item) => {
    const categoryId = getCategoryId(item);

    // If we have a categoryId, get its name
    if (categoryId && menuMap?.categories?.[categoryId]) {
      return getTranslationValue(menuMap.categories[categoryId].name) || '-';
    }

    return '-';
  };

  const handleOpenAvailabilityMenu = (event, item) => {
    event.stopPropagation();
    setAvailabilityMenuAnchor(event.currentTarget);
    setAvailabilityMenuItem(item);
  };

  const handleCloseAvailabilityMenu = () => {
    setAvailabilityMenuAnchor(null);
    setAvailabilityMenuItem(null);
  };

  const handleChangeAvailability = async (status) => {
    if (!availabilityMenuItem) return;

    setUpdatingAvailabilityItemId(availabilityMenuItem._id);
    handleCloseAvailabilityMenu();

    try {
      const soldOutData = {
        status: status,
        soldOutDate: getSoldOutDate(status, businessTimezone, moment),
      };

      await dispatch(updateItem(availabilityMenuItem._id, { 
        soldOut: soldOutData,
        provider: 'pos',
      }));
      toast.success('Availability updated successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to update availability');
    } finally {
      setUpdatingAvailabilityItemId(null);
    }
  };

  const getStatusChip = (item) => {
    const status =
      filteringSoldOut(item, currentBusiness) || SOLD_OUT_STATUS.AVAILABLE;
    const isUpdating = updatingAvailabilityItemId === item._id;

    const chipProps = {
      size: 'small',
      onClick: (e) => handleOpenAvailabilityMenu(e, item),
      sx: {
        cursor: 'pointer',
        fontWeight: 500,
        '&:hover': {
          opacity: 0.8,
        },
      },
    };

    if (isUpdating) {
      return (
        <Chip
          label={<CircularProgress size={14} color="inherit" />}
          size="small"
          sx={{
            bgcolor: '#e0e0e0',
            minWidth: 80,
          }}
        />
      );
    }

    switch (status) {
      case SOLD_OUT_STATUS.SOLD_OUT_TODAY:
        return (
          <Chip
            {...chipProps}
            label="Sold Out Today"
            sx={{
              ...chipProps.sx,
              bgcolor: '#fff3e0',
              color: '#e65100',
            }}
          />
        );
      case SOLD_OUT_STATUS.SOLD_OUT_PERMANENT:
        return (
          <Chip
            {...chipProps}
            label="Permanently unavailable"
            sx={{
              ...chipProps.sx,
              bgcolor: '#ffebee',
              color: '#c62828',
            }}
          />
        );
      default:
        return (
          <Chip
            {...chipProps}
            label="Available"
            sx={{
              ...chipProps.sx,
              bgcolor: '#e8f5e9',
              color: '#2e7d32',
            }}
          />
        );
    }
  };

  const handleToggleSelect = (itemId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  // The header checkbox covers the rows on the current page only
  const pageItemIds = paginatedItems.map((item) => item._id);
  const isAllSelected =
    pageItemIds.length > 0 && pageItemIds.every((id) => selectedIds.has(id));
  const isIndeterminate =
    !isAllSelected && pageItemIds.some((id) => selectedIds.has(id));

  const handleToggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      pageItemIds.forEach((id) =>
        isAllSelected ? next.delete(id) : next.add(id)
      );
      return next;
    });
  };

  const handleOpenBulkAction = (type) => {
    setBulkActionType(type);
    if (type === 'price') setBulkPrice('');
    if (type === 'availability') setBulkAvailability(SOLD_OUT_STATUS.AVAILABLE);
  };

  const handleCloseBulkAction = () => {
    setBulkActionType(null);
    setBulkPrice('');
    setBulkAvailability('');
  };

  const handleBulkPriceChange = async () => {
    const newPrice = parseFloat(bulkPrice);
    if (isNaN(newPrice) || newPrice < 0) {
      toast.error('Please enter a valid price');
      return;
    }
    setBulkActionType(null);
    setIsBulkProcessing(true);
    const ids = [...selectedIds];
    const results = await Promise.allSettled(
      ids.map((id) =>
        dispatch(updateItem(id, { price: newPrice, provider: 'pos' }))
      )
    );
    const succeeded = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed === 0) {
      toast.success(`Price updated for ${succeeded} item(s)`);
    } else {
      toast.warning(`Price updated for ${succeeded} item(s), ${failed} failed`);
    }
    setSelectedIds(new Set());
    setBulkPrice('');
    setIsBulkProcessing(false);
  };

  const handleBulkAvailabilityChange = async () => {
    if (!bulkAvailability) return;
    const status = bulkAvailability;
    setBulkActionType(null);
    setIsBulkProcessing(true);
    const ids = [...selectedIds];
    const soldOutData = {
      status,
      soldOutDate: getSoldOutDate(status, businessTimezone, moment),
    };
    const results = await Promise.allSettled(
      ids.map((id) =>
        dispatch(updateItem(id, { soldOut: soldOutData, provider: 'pos' }))
      )
    );
    const succeeded = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed === 0) {
      toast.success(`Availability updated for ${succeeded} item(s)`);
    } else {
      toast.warning(
        `Availability updated for ${succeeded} item(s), ${failed} failed`
      );
    }
    setSelectedIds(new Set());
    setBulkAvailability('');
    setIsBulkProcessing(false);
  };

  const handleBulkDelete = async () => {
    setBulkActionType(null);
    setIsBulkProcessing(true);
    const ids = [...selectedIds];
    const results = await Promise.allSettled(
      ids.map((id) => dispatch(deleteItem(id)))
    );
    const succeeded = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed === 0) {
      toast.success(`${succeeded} item(s) deleted`);
    } else {
      toast.warning(`${succeeded} item(s) deleted, ${failed} failed`);
    }
    setSelectedIds(new Set());
    setIsBulkProcessing(false);
  };

  const isPageLoading =
    isBulkProcessing || duplicatingItemId !== null || isDeleting;

  // Show loading while providers are loading or haven't been fetched yet
  if (providersLoading || !providersLoaded) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 64px)',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Only show "POS Not Connected" after providers have finished loading
  if (!isPosConnected) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 64px)',
          bgcolor: '#f5f5f5',
          p: 3,
        }}
      >
        <Paper
          sx={{
            p: 4,
            maxWidth: 500,
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            POS Not Connected
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
            Please connect your business to a POS system to access the POS Menu
            Items.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Go to <strong>Providers</strong> to connect your POS system.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Items Management</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddItem}
        >
          Add Item
        </Button>
      </Box>

      <Card>
        <CardContent>
          <TextField
            label="Search Items"
            variant="outlined"
            fullWidth
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
              setSelectedIds(new Set());
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />

          {menuLoadStatus === 'error' ? (
            <MenuLoadError onRetry={retryMenuLoad} />
          ) : menuLoadStatus !== 'ready' ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ position: 'relative' }}>
                {selectedIds.size > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 58,
                      right: 0,
                      height: 57,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      bgcolor: '#e3f2fd',
                      zIndex: 3,
                      pl: 2,
                      borderBottom: '1px solid rgba(224,224,224,1)',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mr: 1 }}
                    >
                      {selectedIds.size} selected
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleOpenBulkAction('price')}
                      disabled={isBulkProcessing}
                      sx={{ textTransform: 'none' }}
                    >
                      Price Change
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleOpenBulkAction('availability')}
                      disabled={isBulkProcessing}
                      sx={{ textTransform: 'none' }}
                    >
                      Availability Change
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => handleOpenBulkAction('delete')}
                      disabled={isBulkProcessing}
                      sx={{ textTransform: 'none' }}
                    >
                      Delete
                    </Button>
                  </Box>
                )}
                <TableContainer>
                  <Table sx={{ tableLayout: 'auto' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" sx={{ width: 48 }}>
                          <Checkbox
                            indeterminate={isIndeterminate}
                            checked={isAllSelected}
                            onChange={handleToggleSelectAll}
                            disabled={sortedItems.length === 0}
                          />
                        </TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell align="right" sx={{ width: 100 }}>
                          Price
                        </TableCell>
                        <TableCell sx={{ width: 120 }}>Status</TableCell>
                        <TableCell align="right" sx={{ width: 130 }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                  <TableBody>
                    {paginatedItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          {searchTerm
                            ? `No items found matching "${searchTerm}"`
                            : 'No items found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedItems.map((item) => (
                        <TableRow
                          key={item._id}
                          hover
                          selected={selectedIds.has(item._id)}
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleEditItem(item._id)}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedIds.has(item._id)}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => handleToggleSelect(item._id)}
                            />
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              {item.image?.url && (
                                <Box
                                  component="img"
                                  src={item.image.url}
                                  alt={getTranslationValue(item.name)}
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    objectFit: 'cover',
                                    borderRadius: 1,
                                  }}
                                />
                              )}
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {getTranslationValue(item.name)}
                                </Typography>
                                {item.title?.en && (
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                  >
                                    {getTranslationValue(item.title)}
                                  </Typography>
                                )}
                                {item.isPopular && (
                                  <Chip
                                    label="Popular"
                                    size="small"
                                    color="primary"
                                    sx={{
                                      mt: 0.5,
                                      height: 18,
                                      fontSize: '10px',
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>{getCategoryName(item)}</TableCell>
                          <TableCell align="right">
                            ${(Number(item.price) || 0).toFixed(2)}
                          </TableCell>
                          <TableCell>{getStatusChip(item)}</TableCell>
                          <TableCell align="right">
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                gap: 0.5,
                              }}
                            >
                              <IconButton
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditItem(item._id);
                                }}
                                size="small"
                                title="Edit"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                color="default"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenDuplicateDialog(item);
                                }}
                                size="small"
                                title="Duplicate"
                                disabled={duplicatingItemId === item._id}
                              >
                                {duplicatingItemId === item._id ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <ContentCopyIcon />
                                )}
                              </IconButton>
                              <IconButton
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenDeleteDialog(item);
                                }}
                                size="small"
                                title="Delete"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              </Box>

              <TablePagination
                component="div"
                count={sortedItems.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{
                  '& .MuiTablePagination-toolbar': {
                    flexWrap: 'nowrap',
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
                    {
                      margin: 0,
                    },
                }}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete &quot;
            {getTranslationValue(deletingItem?.name) || 'this item'}&quot;? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary" disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteItem}
            color="error"
            variant="contained"
            autoFocus
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Availability Menu */}
      <Menu
        anchorEl={availabilityMenuAnchor}
        open={Boolean(availabilityMenuAnchor)}
        onClose={handleCloseAvailabilityMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <MenuItem
          onClick={() => handleChangeAvailability(SOLD_OUT_STATUS.AVAILABLE)}
          selected={
            availabilityMenuItem
              ? filteringSoldOut(availabilityMenuItem, currentBusiness) ===
                  SOLD_OUT_STATUS.AVAILABLE ||
                !filteringSoldOut(availabilityMenuItem, currentBusiness)
              : false
          }
        >
          <ListItemIcon>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: '#4caf50',
              }}
            />
          </ListItemIcon>
          Available
        </MenuItem>
        <MenuItem
          onClick={() => handleChangeAvailability(SOLD_OUT_STATUS.SOLD_OUT_TODAY)}
          selected={
            availabilityMenuItem
              ? filteringSoldOut(availabilityMenuItem, currentBusiness) ===
                SOLD_OUT_STATUS.SOLD_OUT_TODAY
              : false
          }
        >
          <ListItemIcon>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: '#ff9800',
              }}
            />
          </ListItemIcon>
          Sold Out Today
        </MenuItem>
        <MenuItem
          onClick={() => handleChangeAvailability(SOLD_OUT_STATUS.SOLD_OUT_PERMANENT)}
          selected={
            availabilityMenuItem
              ? filteringSoldOut(availabilityMenuItem, currentBusiness) ===
                SOLD_OUT_STATUS.SOLD_OUT_PERMANENT
              : false
          }
        >
          <ListItemIcon>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: '#f44336',
              }}
            />
          </ListItemIcon>
          Sold Out Permanently
        </MenuItem>
      </Menu>

      {/* Bulk Price Change Dialog */}
      <Dialog
        open={bulkActionType === 'price'}
        onClose={handleCloseBulkAction}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Change Price for {selectedIds.size} Item(s)</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the new price to apply to all selected items.
          </DialogContentText>
          <TextField
            autoFocus
            label="New Price"
            type="number"
            fullWidth
            value={bulkPrice}
            onChange={(e) => setBulkPrice(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: 0, step: '0.01' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkAction} disabled={isBulkProcessing}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkPriceChange}
            variant="contained"
            disabled={!bulkPrice || isBulkProcessing}
            startIcon={
              isBulkProcessing ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            {isBulkProcessing ? 'Updating...' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Availability Change Dialog */}
      <Dialog
        open={bulkActionType === 'availability'}
        onClose={handleCloseBulkAction}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Change Availability for {selectedIds.size} Item(s)
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Select the new availability status for all selected items.
          </DialogContentText>
          <FormControl>
            <RadioGroup
              value={bulkAvailability}
              onChange={(e) => setBulkAvailability(e.target.value)}
            >
              <FormControlLabel
                value={SOLD_OUT_STATUS.AVAILABLE}
                control={<Radio />}
                label="Available"
              />
              <FormControlLabel
                value={SOLD_OUT_STATUS.SOLD_OUT_TODAY}
                control={<Radio />}
                label="Sold Out Today"
              />
              <FormControlLabel
                value={SOLD_OUT_STATUS.SOLD_OUT_PERMANENT}
                control={<Radio />}
                label="Sold Out Permanently"
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkAction} disabled={isBulkProcessing}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkAvailabilityChange}
            variant="contained"
            disabled={!bulkAvailability || isBulkProcessing}
            startIcon={
              isBulkProcessing ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            {isBulkProcessing ? 'Updating...' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog
        open={bulkActionType === 'delete'}
        onClose={handleCloseBulkAction}
      >
        <DialogTitle>Delete {selectedIds.size} Item(s)</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selectedIds.size} selected item(s)?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkAction} disabled={isBulkProcessing}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkDelete}
            color="error"
            variant="contained"
            disabled={isBulkProcessing}
            startIcon={
              isBulkProcessing ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            {isBulkProcessing ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Item Dialog */}
      <Dialog
        open={duplicateDialogOpen}
        onClose={handleCloseDuplicateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Duplicate Item</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the name and internal name for the duplicated item.
          </DialogContentText>
          <TextField
            autoFocus
            label="Name"
            fullWidth
            required
            value={duplicateName}
            onChange={(e) => setDuplicateName(e.target.value)}
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Internal Name (optional)"
            fullWidth
            value={duplicateTitle}
            onChange={(e) => setDuplicateTitle(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="Used for internal reference only"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDuplicateDialog}
            disabled={duplicatingItemId !== null}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDuplicate}
            variant="contained"
            disabled={!duplicateName.trim() || duplicatingItemId !== null}
            startIcon={duplicatingItemId ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {duplicatingItemId ? 'Creating...' : 'Create Copy'}
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop
        open={isPageLoading}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          bgcolor: 'rgba(255, 255, 255, 0.7)',
          color: '#1976d2',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress color="inherit" size={48} />
        <Typography variant="body1" sx={{ fontWeight: 500, color: '#1976d2' }}>
          Processing...
        </Typography>
      </Backdrop>
    </Box>
  );
};

export default PosMenuItems;
